import { FormErrorMessage, FormFieldGroup, FormLabel, FormLabelDescription, Text } from '$components'
import { PricingVariantCard, PricingVariantFormValue } from './pricing-variant-card'

type SelectOption = {
  id: string
  value: string
}

type PricingVariantListProps = {
  variants: PricingVariantFormValue[]
  fieldName: string
  currency?: string
  sizeOptions: SelectOption[]
  coatOptions: SelectOption[]
  breedOptions?: SelectOption[]
  onAdd: (type: 'size-coat' | 'breed') => void
  onRemove: (index: number) => void
  onDurationChange: (index: number, duration: number) => void
  error?: string
  addLabel?: string
}

export function PricingVariantList({
  variants,
  fieldName,
  currency,
  sizeOptions,
  coatOptions,
  breedOptions,
  onAdd,
  onRemove,
  onDurationChange,
  error,
}: PricingVariantListProps) {
  // Calculate override types from variant data
  const overrideTypes = variants.map((v) => {
    if (v.is_default) { return null }
    if (v.breed) { return 'breed' }
    if (v.size_category && v.coat_type) { return 'size-coat' }
    // Use stored override_type for new variants that don't have values yet
    return v.override_type || null
  })

  // Get all specific coat types (excluding "All")
  const specificCoatTypes = coatOptions.filter((opt) => opt.value !== 'All').map((opt) => opt.value)

  // Get filtered options for each variant to prevent duplicate combinations
  const getFilteredOptions = (currentIndex: number) => {
    const currentVariant = variants[currentIndex]
    const currentSize = currentVariant?.size_category?.value
    const currentCoat = currentVariant?.coat_type?.value
    const currentBreed = currentVariant?.breed?.value

    // Get all existing size/coat combinations (excluding breed overrides)
    const existingSizeCoatCombinations = variants
      .map((v, idx) => {
        if (idx === currentIndex || v.is_default || v.breed) {return null}
        const size = v.size_category?.value
        const coat = v.coat_type?.value
        if (size && coat) {
          return { size, coat }
        }
        return null
      })
      .filter((c): c is { size: string; coat: string } => c !== null)

    // Get existing breeds
    const existingBreeds = variants
      .map((v, idx) => {
        if (idx === currentIndex || v.is_default || !v.breed) {return null}
        return v.breed.value
      })
      .filter((b): b is string => b !== null)

    // Filter breed options to exclude already used breeds
    const filteredBreedOptions = (breedOptions || []).filter((breedOption) => {
      if (currentBreed === breedOption.value) {
        return true // Allow current breed
      }
      return !existingBreeds.includes(breedOption.value)
    })

    // For each size, check if all specific coat types are already used
    // Include the current variant's combination if it matches the size
    const getUsedCoatTypesForSize = (size: string) => {
      const usedCoatTypes = existingSizeCoatCombinations
        .filter((combo) => combo.size === size)
        .map((combo) => combo.coat)
        .filter((coat) => coat !== 'All') // Only count specific coat types

      // Include current variant's coat if it matches the size and is not "All"
      if (currentSize === size && currentCoat && currentCoat !== 'All' && !currentBreed) {
        if (!usedCoatTypes.includes(currentCoat)) {
          usedCoatTypes.push(currentCoat)
        }
      }

      return usedCoatTypes
    }

    // Check if a size has any specific overrides (non-All coat types)
    // Include the current variant's combination if it matches the size
    const hasSpecificOverridesForSize = (size: string) => {
      const hasInOtherVariants = existingSizeCoatCombinations.some(
        (combo) => combo.size === size && combo.coat !== 'All',
      )

      // Also check if current variant has a specific override for this size
      const hasInCurrentVariant = currentSize === size && currentCoat && currentCoat !== 'All' && !currentBreed

      return hasInOtherVariants || hasInCurrentVariant
    }

    // Filter size options: exclude sizes that would create a duplicate combination or if all coat types are used
    const filteredSizeOptions = sizeOptions.filter((sizeOption) => {
      // Skip if this is a breed override
      if (currentBreed) {
        return false
      }

      // If current variant has a coat selected, check if this size would create a duplicate
      if (currentCoat) {
        const wouldBeDuplicate = existingSizeCoatCombinations.some(
          (combo) => combo.size === sizeOption.value && combo.coat === currentCoat,
        )
        if (wouldBeDuplicate) {
          return false
        }
      }

      // Check if all specific coat types for this size are already used
      const usedCoatTypes = getUsedCoatTypesForSize(sizeOption.value)
      const allCoatTypesUsed = specificCoatTypes.every((coatType) => usedCoatTypes.includes(coatType))

      // If all coat types are used and current variant doesn't have this size selected, hide it
      if (allCoatTypesUsed && currentSize !== sizeOption.value) {
        return false
      }

      return true
    })

    // Filter coat options: exclude coats that would create a duplicate combination
    // Also hide "All" if there are specific overrides for the current size
    const filteredCoatOptions = coatOptions.filter((coatOption) => {
      // Skip if this is a breed override
      if (currentBreed) {
        return false
      }

      // If current variant has a size selected
      if (currentSize) {
        // Hide "All" if there are any specific overrides for this size
        if (coatOption.value === 'All') {
          return !hasSpecificOverridesForSize(currentSize)
        }

        // Check if this coat would create a duplicate combination
        const wouldBeDuplicate = existingSizeCoatCombinations.some(
          (combo) => combo.size === currentSize && combo.coat === coatOption.value,
        )
        return !wouldBeDuplicate
      }

      // If no size selected yet, allow all coats
      return true
    })

    return {
      sizeOptions: filteredSizeOptions,
      coatOptions: filteredCoatOptions,
      breedOptions: filteredBreedOptions,
    }
  }

  return (
    <FormFieldGroup>
      <div className="mb-2">
        <FormLabel htmlFor={fieldName}>
          Pricing
        </FormLabel>

        <FormLabelDescription>
          Set the base price that applies to every breed, then add overrides for specific size and coat combinations or specific breeds as needed.
        </FormLabelDescription>
      </div>

      <div className="flex flex-col gap-4">
        {variants.map((variant, index) => {
          const { sizeOptions: filteredSizeOptions, coatOptions: filteredCoatOptions, breedOptions: filteredBreedOptions } = getFilteredOptions(index)
          const overrideType = overrideTypes[index]

          return (
            <PricingVariantCard
              key={`${variant.is_default ? 'default' : 'override'}-${index}`}
              baseFieldName={fieldName}
              index={index}
              variant={variant}
              currency={currency}
              sizeOptions={filteredSizeOptions}
              coatOptions={filteredCoatOptions}
              breedOptions={filteredBreedOptions}
              overrideType={overrideType}
              onDurationChange={onDurationChange}
              onRemove={onRemove}
              canRemove={variants.length > 1 && !variant.is_default}
            />
          )
        })}
      </div>

      <div className="pt-2 flex w-full justify-center">
        <Text
          type="s"
          className="text-gray-600"
        >
          Need more flexible pricing?{' '}
          <button
            type="button"
            onClick={() => onAdd('size-coat')}
            className="text-gray-900 hover:text-indigo-500 underline"
          >
            Adjust by size and coat
          </button>
          {' '}or{' '}
          <button
            type="button"
            onClick={() => onAdd('breed')}
            className="text-gray-900 hover:text-indigo-500 underline"
          >
            specific breed
          </button>
          .
        </Text>
      </div>

      <FormErrorMessage error={error} />
    </FormFieldGroup>
  )
}
